// Ticket detail — title, status, priority, assignee, description, comments.
// Internal vs external comments are distinguished with a tab; both are
// stored in the same collection and have an `is_internal` flag.
import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { useSubscription } from '../../contexts/SubscriptionContext';
import { ArrowLeft, ChatCircle, LockSimple, Warning } from '@phosphor-icons/react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import ServiceDeskUpsell from '../../components/serviceDesk/ServiceDeskUpsell';
import ServiceDeskAttachments from '../../components/serviceDesk/ServiceDeskAttachments';
import { CsatBadge } from '../../components/serviceDesk/CsatWidget';
import SlaCard from '../../components/serviceDesk/SlaCard';
import CiLinkPanel from '../../components/serviceDesk/CiLinkPanel';
import IncidentProblemPanel from '../../components/serviceDesk/IncidentProblemPanel';
import KbSuggestPanel from '../../components/serviceDesk/KbSuggestPanel';
import TicketFields from '../../components/serviceDesk/TicketFields';
import RichEditor, { RichEditorContent } from '../../components/RichEditor';
import { StatusBadge, Dot, STATUS_DOT, PRIORITY_DOT, STATUS_OPTIONS, PRIORITY_OPTIONS, statusLabel, priorityLabel } from '../../components/serviceDesk/ticketMeta';

const initials = (name) => (name || '?').trim().split(/\s+/).slice(0, 2).map((s) => s[0]?.toUpperCase()).join('') || '?';
const AVATAR_TONE = { amber: 'bg-amber-500', blue: 'bg-blue-500', zinc: 'bg-zinc-400' };
const Avatar = ({ name, tone = 'zinc' }) => (
  <div className={`w-8 h-8 rounded-full ${AVATAR_TONE[tone]} text-white text-[11px] font-bold flex items-center justify-center shrink-0`}>
    {initials(name)}
  </div>
);

export default function TicketDetailPage() {
  const { ticketId } = useParams();
  const { API, getOrgHeaders, currentOrg, user } = useAuth();
  const { caps } = useSubscription();
  const navigate = useNavigate();
  const [ticket, setTicket] = useState(null);
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newComment, setNewComment] = useState('');
  const [isInternal, setIsInternal] = useState(false);
  const [members, setMembers] = useState([]);
  const [directory, setDirectory] = useState([]);
  const [saving, setSaving] = useState(false);

  const sdEnabled = caps?.service_desk_enabled === true;

  const load = useCallback(async () => {
    if (!currentOrg || !sdEnabled) return;
    setLoading(true);
    try {
      const [t, c] = await Promise.all([
        axios.get(`${API}/service-desk/tickets/${ticketId}`, { withCredentials: true, headers: getOrgHeaders() }),
        axios.get(`${API}/service-desk/tickets/${ticketId}/comments`, { withCredentials: true, headers: getOrgHeaders() }),
      ]);
      setTicket(t.data);
      setComments(c.data || []);
      // Assignable people for THIS portal = staffed agents + admins. Also powers
      // @mentions in agent comments (internal only).
      const pid = t.data.portal_id;
      if (pid) {
        const ag = await axios.get(`${API}/service-desk/portals/${pid}/agents`,
          { withCredentials: true, headers: getOrgHeaders() }).catch(() => ({ data: { members: [] } }));
        setMembers((ag.data?.members || []).filter(m => m.is_agent || m.all_portals));
      }
      // Full org directory (incl. deactivated agents) so a removed agent's
      // past comments resolve to their real name, not a generic "Agent".
      axios.get(`${API}/members/directory`, { withCredentials: true, headers: getOrgHeaders() })
        .then(r => setDirectory(r.data || [])).catch(() => {});
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to load ticket');
    } finally { setLoading(false); }
  }, [currentOrg, sdEnabled, ticketId, API, getOrgHeaders]);

  useEffect(() => { load(); }, [load]);

  if (!sdEnabled) return <ServiceDeskUpsell />;

  const updateField = async (patch) => {
    setSaving(true);
    try {
      const { data } = await axios.patch(`${API}/service-desk/tickets/${ticketId}`, patch,
        { withCredentials: true, headers: getOrgHeaders() });
      setTicket(prev => ({ ...prev, ...data }));
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to update');
    } finally { setSaving(false); }
  };

  const addComment = async () => {
    const body = newComment.trim();
    if (!body) return;
    try {
      const { data } = await axios.post(`${API}/service-desk/tickets/${ticketId}/comments`,
        { body, is_internal: isInternal },
        { withCredentials: true, headers: getOrgHeaders() });
      setComments(prev => [...prev, data]);
      setNewComment('');
      toast.success('Comment added');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to add comment');
    }
  };

  if (loading) return <div className="flex-1 flex items-center justify-center text-zinc-400">Loading…</div>;
  if (!ticket) return <div className="flex-1 flex items-center justify-center text-zinc-400">Ticket not found</div>;

  const memberById = Object.fromEntries(members.map(m => [m.user_id, m]));
  const dirById = Object.fromEntries(directory.map(m => [m.user_id, m]));
  const resolveAgent = (uid) => memberById[uid] || dirById[uid];
  const hasForm = (ticket.request_type?.form_fields?.length > 0) && ticket.form_data && Object.keys(ticket.form_data).length > 0;
  const requesterName = ticket.requester_name || resolveAgent(ticket.requester_user_id)?.name || ticket.requester_email || '—';

  const saveField = async (key, value) => {
    try {
      const { data } = await axios.patch(`${API}/service-desk/tickets/${ticketId}`,
        { form_data: { [key]: value } }, { withCredentials: true, headers: getOrgHeaders() });
      setTicket(prev => ({ ...prev, ...data }));
      toast.success('Field updated');
      return true;
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to update field');
      return false;
    }
  };

  const uploadCommentImage = async (file) => {
    const fd = new FormData();
    fd.append('file', file);
    try {
      const { data } = await axios.post(`${API}/service-desk/tickets/${ticketId}/comment-images`, fd,
        { withCredentials: true, headers: getOrgHeaders() });
      return { url: data.url };
    } catch (err) {
      return { error: err?.response?.data?.detail || 'Upload failed' };
    }
  };

  const mentionItems = members.filter(m => m.user_id && m.name).map(m => ({ user_id: m.user_id, name: m.name, email: m.email, avatar: m.avatar }));

  return (
    <div className="flex-1 overflow-auto bg-zinc-50" data-testid="sd-ticket-detail-page">
      <div className="max-w-[1600px] mx-auto px-8 py-6">
        <button onClick={() => navigate(`/service-desk/portal/${ticket.portal_id}`)}
          className="flex items-center gap-1.5 text-xs text-zinc-500 hover:text-zinc-900 mb-4"
          data-testid="sd-ticket-back">
          <ArrowLeft size={14} /> Back to {ticket.portal?.name || 'queue'}
        </button>
        <div className="flex gap-6 items-start">
          {/* Main */}
          <div className="flex-1 min-w-0 space-y-4">
            <div className="bg-white border border-zinc-200 rounded-lg p-6">
              {ticket.is_major && (
                <div className="-mx-6 -mt-6 mb-4 px-6 py-2.5 bg-red-600 text-white rounded-t-lg flex items-center gap-2 text-sm font-semibold" data-testid="sd-major-banner">
                  <Warning size={16} weight="fill" /> MAJOR INCIDENT — elevated priority &amp; visibility
                </div>
              )}
              <div className="flex items-center gap-2 mb-2">
                <span className="font-mono text-xs text-blue-700 font-bold">{ticket.ticket_key}</span>
                <StatusBadge status={ticket.status} />
                {ticket.source === 'portal' && (
                  <span className="text-[11px] text-zinc-400">· {requesterName} raised this via portal</span>
                )}
              </div>
              <Input value={ticket.title} onChange={(e) => setTicket(prev => ({ ...prev, title: e.target.value }))}
                onBlur={(e) => updateField({ title: e.target.value })}
                className="text-xl font-bold border-none px-0 focus-visible:ring-0 mb-1"
                data-testid="sd-ticket-title" />
              {hasForm ? (
                <div className="mt-4 border-t border-zinc-100 pt-4" data-testid="sd-ticket-request-details">
                  <h3 className="text-[11px] uppercase tracking-wider text-zinc-500 font-semibold mb-1">Request details</h3>
                  <TicketFields fields={ticket.request_type?.form_fields} formData={ticket.form_data} editable onSaveField={saveField} />
                </div>
              ) : (
                <Textarea rows={6} value={ticket.description || ''}
                  onChange={(e) => setTicket(prev => ({ ...prev, description: e.target.value }))}
                  onBlur={(e) => updateField({ description: e.target.value })}
                  placeholder="Add a description…"
                  className="text-sm mt-3"
                  data-testid="sd-ticket-description" />
              )}
            </div>

            {/* Attachments — agent view, can mark internal */}
            <div className="bg-white border border-zinc-200 rounded-lg p-6">
              <h3 className="text-sm font-semibold text-zinc-900 mb-3 flex items-center gap-1.5">
                Attachments
              </h3>
              <ServiceDeskAttachments
                listUrl={`${API}/service-desk/tickets/${ticketId}/attachments`}
                uploadUrl={`${API}/service-desk/tickets/${ticketId}/attachments`}
                deleteUrl={(id) => `${API}/service-desk/attachments/${id}`}
                canUploadInternal={true}
                withCredentials={true}
                headers={getOrgHeaders()}
              />
            </div>

            {/* Activity / Comments — Jira Service Management style */}
            <div className="bg-white border border-zinc-200 rounded-lg p-6">
              <h3 className="text-sm font-semibold text-zinc-900 mb-4 flex items-center gap-1.5">
                <ChatCircle size={15} weight="fill" className="text-zinc-400" /> Activity
              </h3>

              {/* Composer at the top, with internal-note / reply toggle */}
              <div className="flex gap-3 mb-6">
                <Avatar name={user?.name} tone="blue" />
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2 text-sm font-semibold">
                    <button onClick={() => setIsInternal(true)}
                      className={`transition-colors ${isInternal ? 'text-amber-700' : 'text-zinc-400 hover:text-zinc-600'}`}
                      data-testid="sd-comment-tab-internal">Add internal note</button>
                    <span className="text-zinc-300 font-normal">/</span>
                    <button onClick={() => setIsInternal(false)}
                      className={`transition-colors ${!isInternal ? 'text-blue-700' : 'text-zinc-400 hover:text-zinc-600'}`}
                      data-testid="sd-comment-tab-reply">Reply to customer</button>
                  </div>
                  <div className={isInternal ? 'rounded-lg ring-1 ring-amber-200' : ''} data-testid="sd-comment-input">
                    <RichEditor value={newComment} onChange={setNewComment} uploadImage={uploadCommentImage}
                      mentionItems={mentionItems} minHeight={90}
                      placeholder={isInternal ? 'Add an internal note (not visible to the requester)… paste screenshots directly' : 'Reply to the customer… paste screenshots directly'} />
                  </div>
                  <div className="mt-2 flex items-center justify-between gap-3">
                    <KbSuggestPanel ticketId={ticketId} seedQuery={ticket.title} category={ticket.category}
                      onInsert={(html) => setNewComment(prev => (prev && prev !== '<p></p>' ? prev : '') + html)} />
                    <Button size="sm" onClick={addComment} disabled={!newComment.trim()} data-testid="sd-comment-submit"
                      className={isInternal ? 'bg-amber-600 hover:bg-amber-700' : ''}>
                      {isInternal ? 'Add internal note' : 'Reply to customer'}
                    </Button>
                  </div>
                </div>
              </div>

              {/* Comment thread */}
              <div className="space-y-4">
                {comments.length === 0 && <p className="text-xs text-zinc-400">No activity yet.</p>}
                {[...comments].reverse().map(c => {
                  const isCust = !!c.author_customer_id;
                  const jiraName = c.imported_from === 'jira' && c.imported_author_name ? c.imported_author_name : null;
                  const name = jiraName
                    || (isCust
                    ? (ticket.requester_name || 'Customer')
                    : (resolveAgent(c.author_user_id)?.name || (c.author_user_id === user?.user_id ? user?.name : null) || 'Agent'));
                  const tone = c.is_internal ? 'amber' : isCust ? 'zinc' : 'blue';
                  return (
                    <div key={c.comment_id} className="flex gap-3" data-testid={`sd-comment-${c.comment_id}`}>
                      <Avatar name={name} tone={tone} />
                      <div className={`flex-1 rounded-lg p-3 ${c.is_internal ? 'bg-amber-50 border border-amber-200' : 'border border-zinc-200 bg-white'}`}>
                        <div className="flex items-center justify-between mb-1 gap-2">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-sm font-semibold text-zinc-900">{name}</span>
                            {jiraName && <span className="text-[9px] px-1.5 py-0.5 rounded bg-blue-50 text-blue-600 font-semibold uppercase tracking-wide" data-testid={`sd-comment-via-jira-${c.comment_id}`}>via Jira</span>}
                            {c.is_internal ? (
                              <span className="text-[10px] font-bold uppercase tracking-wider text-amber-700 flex items-center gap-1">
                                <LockSimple size={9} weight="fill" /> Internal note
                              </span>
                            ) : (
                              <span className="text-[10px] text-zinc-400">{isCust ? 'Customer' : 'replied to customer'}</span>
                            )}
                          </div>
                          <span className="text-[11px] text-zinc-500 shrink-0">{new Date(c.created_at).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' })}</span>
                        </div>
                        <div className="text-sm text-zinc-700 break-words"><RichEditorContent html={c.body} /></div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Right panel */}
          <div className="w-[340px] shrink-0 space-y-4">
            <div className="bg-white border border-zinc-200 rounded-lg p-4 space-y-3">
              <div>
                <Label className="text-[11px] uppercase tracking-wider text-zinc-500">Status</Label>
                <Select value={ticket.status} onValueChange={(v) => updateField({ status: v })}>
                  <SelectTrigger className="mt-1 h-9" data-testid="sd-ticket-status-select">
                    <span className="flex items-center gap-2"><Dot className={STATUS_DOT[ticket.status]} />{statusLabel(ticket.status)}</span>
                  </SelectTrigger>
                  <SelectContent>
                    {STATUS_OPTIONS.map(s => (
                      <SelectItem key={s} value={s}>
                        <span className="flex items-center gap-2"><Dot className={STATUS_DOT[s]} />{statusLabel(s)}</span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[11px] uppercase tracking-wider text-zinc-500">Priority</Label>
                <Select value={ticket.priority} onValueChange={(v) => updateField({ priority: v })}>
                  <SelectTrigger className="mt-1 h-9" data-testid="sd-ticket-priority-select">
                    <span className="flex items-center gap-2"><Dot className={PRIORITY_DOT[ticket.priority]} />{priorityLabel(ticket.priority)}</span>
                  </SelectTrigger>
                  <SelectContent>
                    {PRIORITY_OPTIONS.map(p => (
                      <SelectItem key={p} value={p}>
                        <span className="flex items-center gap-2"><Dot className={PRIORITY_DOT[p]} />{priorityLabel(p)}</span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[11px] uppercase tracking-wider text-zinc-500">Assignee</Label>
                <Select value={ticket.assignee_user_id || '__none'} onValueChange={(v) => updateField({ assignee_user_id: v === '__none' ? null : v })}>
                  <SelectTrigger className="mt-1 h-9" data-testid="sd-ticket-assignee-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none">
                      <span className="flex items-center gap-2 text-zinc-500"><span className="w-5 h-5 rounded-full bg-zinc-100 ring-1 ring-zinc-200 flex items-center justify-center text-[10px]">—</span> Unassigned</span>
                    </SelectItem>
                    {members.map(m => (
                      <SelectItem key={m.user_id} value={m.user_id}>
                        <span className="flex items-center gap-2">
                          <span className="w-5 h-5 rounded-full bg-blue-100 text-blue-700 text-[9px] font-semibold flex items-center justify-center">{(m.name || m.email || '?').charAt(0).toUpperCase()}</span>
                          {m.name || m.email}
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="pt-2 border-t border-zinc-100 text-[11px] text-zinc-500 space-y-1">
                <div>Portal: <span className="text-zinc-700 font-medium">{ticket.portal?.name}</span></div>
                {ticket.request_type?.name && <div>Request type: <span className="text-zinc-700 font-medium">{ticket.request_type.name}</span></div>}
                <div>Requester: <span className="text-zinc-700 font-medium">{requesterName}</span></div>
                {ticket.requester_email && ticket.requester_email !== requesterName && (
                  <div>Email: <span className="text-zinc-700 font-medium">{ticket.requester_email}</span></div>
                )}
                <div>Created: <span className="text-zinc-700 font-medium">{new Date(ticket.created_at).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' })}</span></div>
                {ticket.updated_at && <div>Updated: <span className="text-zinc-700 font-medium">{new Date(ticket.updated_at).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' })}</span></div>}
              </div>
              {saving && <p className="text-[10px] text-zinc-400">Saving…</p>}
            </div>

            {ticket.sla_state && <SlaCard slaState={ticket.sla_state} />}

            <div className="bg-white border border-zinc-200 rounded-lg p-4">
              <IncidentProblemPanel ticket={ticket} API={API} h={() => ({ withCredentials: true, headers: getOrgHeaders() })} onUpdate={updateField} onOpenTicket={(t) => navigate(`/service-desk/portal/${t.portal_id}/ticket/${t.ticket_id}`)} />
            </div>

            <div className="bg-white border border-zinc-200 rounded-lg p-4">
              <CiLinkPanel targetType="ticket" targetId={ticket.ticket_id} />
            </div>

            {ticket.csat && (
              <div className="bg-white border border-zinc-200 rounded-lg p-4" data-testid="sd-ticket-csat">
                <Label className="text-[11px] uppercase tracking-wider text-zinc-500">Customer satisfaction</Label>
                <div className="mt-1.5"><CsatBadge csat={ticket.csat} /></div>
                {ticket.csat.comment && <p className="text-xs text-zinc-600 mt-2 italic">{`"${ticket.csat.comment}"`}</p>}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
